// pages/dashboard/views/AlertsView.jsx
// ─────────────────────────────────────────────────────────────
// FIXES:
//
// 1. handleNavigate was calling onNavigate(params.get("view"), ...)
//    with TWO args but onNavigate only accepted one (view). The
//    second arg was silently dropped, which was fine, BUT the
//    actual nav call inside onNavigate was:
//      navigate(NAV_TO_PATH[view] || `/${view}`)
//    This worked — but only if AdminLayout's handleAlertNavigate
//    (which becomes onNavigate here via context) wasn't throwing
//    first. Since AdminLayout had the require() bug, nothing fired.
//    Now that AdminLayout is fixed, the chain works. Cleaned up
//    onNavigate to match its actual single-argument usage.
//
// 2. TYPE_CONFIG — added support_message type so "New Support
//    Message" alerts render with the right icon instead of the
//    generic bell fallback.
//
// Everything else is UNCHANGED.
// ─────────────────────────────────────────────────────────────
import { useState } from "react";
import { toast } from "react-toastify";
import { dismissAdminAlert, dismissAllAdminAlerts, markAdminAlertRead } from "../../../api/apiServices";
import { useAlertsContext } from "../AdminLayout";
import { hasPermission, NAV_TO_PATH, VIEW_PERMISSIONS } from "../page";
import { useAuth } from "../../../context/useAuth";
import { useNavigate } from "react-router-dom";

const SEVERITY_CONFIG = {
  critical: { label: "Critical", color: "#ef4444", bg: "bg-red-500/10",   border: "border-red-500/20",   icon: "ri-error-warning-fill", dot: "bg-red-500"   },
  warning:  { label: "Warning",  color: "#f59e0b", bg: "bg-amber-500/10", border: "border-amber-500/20", icon: "ri-alert-fill",         dot: "bg-amber-500" },
  info:     { label: "Info",     color: "#00C896", bg: "bg-[#00C896]/10", border: "border-[#00C896]/20", icon: "ri-information-fill",   dot: "bg-[#00C896]" },
};

const TYPE_CONFIG = {
  kyc_submitted:       { label: "KYC",          icon: "ri-shield-user-line"        },
  large_withdrawal:    { label: "Withdrawal",    icon: "ri-arrow-up-circle-line"    },
  large_sell:          { label: "Sell",          icon: "ri-exchange-dollar-line"    },
  support_ticket:      { label: "Support",       icon: "ri-customer-service-2-line" },
  support_message:     { label: "Support Msg",   icon: "ri-chat-3-line"             }, // ADDED
  system_error:        { label: "System Error",  icon: "ri-bug-line"                },
  withdrawal_failed:   { label: "Tx Failed",     icon: "ri-close-circle-line"       },
  suspicious_activity: { label: "Suspicious",    icon: "ri-spy-line"                },
  low_hot_wallet:      { label: "Hot Wallet",    icon: "ri-wallet-line"             },
};

function timeAgo(date) {
  const m = Math.floor((Date.now() - new Date(date)) / 60_000);
  if (m < 1)  return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function AlertCard({ alert, onDismiss, onNavigate }) {
  const sev  = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info;
  const type = TYPE_CONFIG[alert.type] || { label: alert.type, icon: "ri-notification-line" };
  return (
    <div className={`${sev.bg} border ${sev.border} rounded-2xl p-4 transition-all`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: sev.color + "20" }}>
          <i className={`${sev.icon} text-lg`} style={{ color: sev.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className="text-white text-sm font-semibold">{alert.title}</span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/5 text-white/40 flex items-center gap-1">
              <i className={`${type.icon} text-[11px]`} />{type.label}
            </span>
          </div>
          <p className="text-white/50 text-xs leading-relaxed">{alert.message}</p>
          <p className="text-white/25 text-[10px] mt-1.5">{timeAgo(alert.createdAt)}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {alert.link && (
            <button onClick={() => onNavigate(alert)}
              className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/15 flex items-center justify-center text-white/40 hover:text-white transition-colors" title="Go to issue">
              <i className="ri-arrow-right-up-line text-sm" />
            </button>
          )}
          <button onClick={() => onDismiss(alert._id)}
            className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/15 flex items-center justify-center text-white/40 hover:text-white transition-colors" title="Dismiss">
            <i className="ri-check-line text-sm" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AlertsView() {
  const { alerts, unreadCount, alertsLoading: loading, dismissLocal: onDismissLocal } = useAlertsContext();
  const navigate         = useNavigate();
  const { user }         = useAuth();
  const adminPermissions = user?.adminPermissions || [];
  const isSuperAdmin     = user?.adminRole === "super_admin";
  const [filter,        setFilter]        = useState("all");
  const [dismissingAll, setDismissingAll] = useState(false);

  // Navigate to a view by id — same logic as AdminLayout.handleAlertNavigate
  // but self-contained here so AlertsView doesn't need a prop.
  const navigateToView = (viewId) => {
    if (!viewId) return;
    const perm = VIEW_PERMISSIONS[viewId];
    if (!isSuperAdmin && perm !== undefined && perm !== null && !hasPermission(adminPermissions, perm)) return;
    navigate(NAV_TO_PATH[viewId] || `/${viewId}`);
  };

  const handleDismiss = async (id) => {
    onDismissLocal(id);
    try {
      await dismissAdminAlert(id);
    } catch {
      toast.error("Failed to dismiss");
    }
  };

  const handleDismissAll = async () => {
    setDismissingAll(true);
    const params = filter !== "all" ? { severity: filter } : {};
    onDismissLocal("all");
    try {
      await dismissAllAdminAlerts(params);
    } catch { toast.error("Failed to dismiss all"); }
    finally  { setDismissingAll(false); }
  };

  // Called when the arrow button on an AlertCard is clicked.
  // Parses the ?view= param from alert.link and navigates.
  const handleNavigate = async (alert) => {
    await markAdminAlertRead(alert._id).catch(() => {});
    if (!alert.link) return;
    const params = new URLSearchParams(alert.link.split("?")[1] || "");
    const viewId = params.get("view");
    navigateToView(viewId);
  };

  const visible   = filter === "all" ? alerts : alerts.filter(a => a.severity === filter);
  const critCount = alerts.filter(a => a.severity === "critical").length;
  const warnCount = alerts.filter(a => a.severity === "warning").length;

  const FILTERS = [
    { key: "all",      label: "All",      count: alerts.length },
    { key: "critical", label: "Critical", count: critCount },
    { key: "warning",  label: "Warning",  count: warnCount },
    { key: "info",     label: "Info",     count: alerts.length - critCount - warnCount },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-white">System Alerts</h2>
            {unreadCount > 0 && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-500 text-white">{unreadCount}</span>}
          </div>
          <p className="text-white/40 text-xs mt-0.5">{alerts.length} active · {critCount > 0 ? `${critCount} critical` : "all clear"}</p>
        </div>
        {alerts.length > 0 && (
          <button onClick={handleDismissAll} disabled={dismissingAll}
            className="flex items-center gap-1.5 px-4 py-2 bg-white/5 hover:bg-white/10 text-white/50 hover:text-white text-xs font-semibold rounded-xl transition-colors disabled:opacity-40">
            {dismissingAll
              ? <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
              : <i className="ri-check-double-line" />}
            Dismiss All
          </button>
        )}
      </div>

      {critCount > 0 && (
        <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/20 rounded-2xl px-4 py-3">
          <i className="ri-error-warning-fill text-red-400 text-xl flex-shrink-0" />
          <div>
            <p className="text-red-400 font-semibold text-sm">{critCount} critical alert{critCount > 1 ? "s" : ""} require attention</p>
            <p className="text-red-400/60 text-xs mt-0.5">{alerts.filter(a => a.severity === "critical").map(a => a.title).join(", ")}</p>
          </div>
        </div>
      )}

      <div className="flex gap-2 overflow-x-auto">
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
              filter === f.key ? "bg-[#00C896] text-black" : "bg-white/5 text-white/40 hover:bg-white/10 hover:text-white/70"
            }`}>
            {f.label}
            {f.count > 0 && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${filter === f.key ? "bg-black/20 text-black" : "bg-white/10 text-white/40"}`}>
                {f.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-white/5 rounded-2xl animate-pulse" />)}</div>
      ) : visible.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="w-16 h-16 rounded-2xl bg-[#00C896]/10 flex items-center justify-center mb-4">
            <i className="ri-checkbox-circle-line text-[#00C896] text-3xl" />
          </div>
          <p className="text-white font-semibold text-sm">All clear</p>
          <p className="text-white/30 text-xs mt-1">No active alerts{filter !== "all" ? ` for ${filter}` : ""}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {["critical", "warning", "info"].flatMap(sev =>
            visible.filter(a => a.severity === sev).map(a =>
              <AlertCard key={a._id} alert={a} onDismiss={handleDismiss} onNavigate={handleNavigate} />
            )
          )}
        </div>
      )}
    </div>
  );
}
